import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { PlugIcon, CodeIcon, SendIcon, SearchIcon, BarChart3Icon, CalendarCheckIcon } from 'lucide-react';
// The capabilities the headline cycles through — each is something credits
// power Giga to do, paired with a lucide glyph shown in a Giga-orange rounded
// square so every item reads as a branded lockup (no third-party purple).
const CAPABILITIES: {
  label: string;
  icon: ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>;
}[] = [{
  label: 'Integrations',
  icon: PlugIcon
}, {
  label: 'Code',
  icon: CodeIcon
}, {
  label: 'Outreach',
  icon: SendIcon
}, {
  label: 'Research',
  icon: SearchIcon
}, {
  label: 'Reports',
  icon: BarChart3Icon
}, {
  label: 'Scheduling',
  icon: CalendarCheckIcon
}];

// The animated trailing word in the headline: "Giga does: ___". Cycles through
// CAPABILITIES on a calm timer, sliding + blurring each in/out. Width is held
// stable by an invisible sizer stack so the centered headline never reflows.
// Respects reduced-motion by rendering the first item statically.
function RotatingCapability() {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % CAPABILITIES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce]);
  const active = CAPABILITIES[index];
  const ActiveIcon = active.icon;
  return <span className="relative inline-grid place-items-start align-baseline" data-id="element-4739" data-mp="20f7rsj21bhsm">
      {/* Invisible sizer — reserves the width of the widest capability so the
          centered headline never shifts as words swap. */}
      <span className="invisible col-start-1 row-start-1 grid" aria-hidden="true" data-id="element-4740" data-mp="1cnk92oexfd1f">
        
        {CAPABILITIES.map(c => {
        const Icon = c.icon;
        return <span key={c.label} className="col-start-1 row-start-1 inline-flex items-center gap-3 whitespace-nowrap" data-id="element-4741" data-mp="1hlu6z6s1crqq">
              
              <span className="inline-flex items-center justify-center rounded-2xl bg-[#EA580C] w-[1em] h-[1em]" data-id="element-4742" data-mp="3el2y35i87o71">
                <Icon size={1} data-id="element-4743" data-mp="39lhwenbo91qm" />
              </span>
              {c.label}
            </span>;
      })}
      </span>
      {/* Visible animated layer, absolutely overlaid on the sizer. */}
      <span className="col-start-1 row-start-1 block relative" data-id="element-4744" data-mp="65aw8qsgtmml">
        <AnimatePresence initial={false} mode="wait" data-id="element-4745" data-mp="64hte0wmnh9q">
          <motion.span key={active.label} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: '0.35em',
          filter: 'blur(6px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: '-0.35em',
          filter: 'blur(6px)'
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="inline-flex items-center gap-3 whitespace-nowrap" data-id="element-4746" data-mp="15y7g0i1a5hd">
            
            <span className="inline-flex items-center justify-center rounded-2xl bg-[#EA580C] text-white w-[1.15em] h-[1.15em] shadow-sm" data-id="element-4747" data-mp="xcsvgnf1lf7z">
              <ActiveIcon size={undefined} className="w-[0.62em] h-[0.62em]" strokeWidth={2.5} data-id="element-4748" data-mp="11dbnoikuknap" />
              
            </span>
            <span data-id="element-4749" data-mp="166e9g4jdusgc">{active.label}</span>
          </motion.span>
        </AnimatePresence>
      </span>
    </span>;
}
function GigaAvatar({
  size = 24
}: {
  size?: number;
}) {
  return <span className="flex shrink-0 items-center justify-center rounded-full bg-[#EA580C]" style={{
    width: size,
    height: size
  }} data-id="element-4750" data-mp="1g1zfcdrkocvd">
      
      <span className="bg-white" style={{
      width: size * 0.56,
      height: size * 0.56,
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-4751" data-mp="1p3yxj9ebpmzw" />
      
    </span>;
}
export function PricingCreditsUseCases() {
  return <section className="py-20 md:py-32 bg-[#FAF8F4] overflow-hidden" data-id="element-4752" data-mp="35itfgkg3b8c8">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4753" data-mp="30la01h7nopip">
        {/* Left-aligned to match the section above it and the home page. */}
        <div className="mb-14 max-w-4xl md:mb-18" data-id="element-4754" data-mp="2csl4bcjz1o75">
          <h2 className="font-title text-[36px] font-bold leading-[1.06] tracking-[-0.03em] text-[#2B211A] sm:text-[46px] md:text-[56px]" data-id="element-4755" data-mp="27wcijgb9qwr0">
            <span className="block" data-id="element-4756" data-mp="2mls6kojf0o30">Credits power everything</span>
            <span className="mt-1 inline-flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-4757" data-mp="2hspkf1gywgwu">
              Giga does:
              <RotatingCapability data-id="element-4758" data-mp="1u2v76isqkqc2" />
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4759" data-mp="1p41vmvx0f9ej">
          {/* Card 1 */}
          <div className="flex flex-col rounded-[22px] border border-[#E7E0D4] bg-white p-7 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)]" data-id="element-4760" data-mp="4lzoksztckmz">
            <span className="mb-3 text-[12px] font-bold uppercase tracking-[0.16em] text-[#EA580C]" data-id="element-4761" data-mp="3rsa7ouiims2i">
              Quick Tasks
            </span>
            <h3 className="mb-2 font-title text-[28px] font-bold leading-none tracking-[-0.025em] text-[#2B211A]" data-id="element-4762" data-mp="3vtvd5usq042o">
              100-300 credits
            </h3>
            <p className="mb-7 text-[14.5px] text-[#6B5D52]" data-id="element-4763" data-mp="scve1jgsr3r2">
              Slack summary + CRM follow-up
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-4764" data-mp="ec2icvere2md">
              <div className="flex gap-3" data-id="element-4765" data-mp="jbotmzb118sj">
                <div className="w-8 h-8 rounded-full bg-[#E7DFD5] flex items-center justify-center text-[#6B5D52] font-bold text-xs shrink-0" data-id="element-4766" data-mp="jam7f4onfuiy">
                  S
                </div>
                <div data-id="element-4767" data-mp="3437eqqiflcsp">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-4768" data-mp="343qwa6465z67">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-4769" data-mp="19m7f99kouacv">
                      Sarah
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-4770" data-mp="14ne8s13tjjm7">9:14 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-4771" data-mp="zubf7sz1ka60">
                    <span className="text-[#EA580C] font-semibold" data-id="element-4772" data-mp="qvg9sj3tn43r">@Giga</span>{' '}
                    summarize yesterday's #sales thread and flag anything I need
                    to follow up on
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-4773" data-mp="gxk0lwug2j8a">
                <div className="mt-1" data-id="element-4774" data-mp="cvyfjo7rayn0">
                  <GigaAvatar size={32} data-id="element-4775" data-mp="cx00ckekqzhx" />
                </div>
                <div data-id="element-4776" data-mp="2bbtby8i7g9j1">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-4777" data-mp="2ba9uwfoq5ug7">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-4778" data-mp="279ze07ze9jmk">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-4779" data-mp="3p69ugtel2k5m">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-4780" data-mp="2s06umih9g5w">9:15 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-4781" data-mp="6t3kd2d1z03y">
                    3 deals discussed. Two on track — Acme closes Friday, Bloom
                    signed. One needs you: DataSync went silent after the demo.
                    I drafted a follow-up in HubSpot, want me to send it?
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="flex flex-col rounded-[22px] border border-[#E7E0D4] bg-white p-7 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)]" data-id="element-4782" data-mp="uixu3b22s8jz">
            <span className="mb-3 text-[12px] font-bold uppercase tracking-[0.16em] text-[#EA580C]" data-id="element-4783" data-mp="zk3rvnalem0d">
              Complex Workflows
            </span>
            <h3 className="mb-2 font-title text-[28px] font-bold leading-none tracking-[-0.025em] text-[#2B211A]" data-id="element-4784" data-mp="675dz455mess">
              500-1,500 credits
            </h3>
            <p className="mb-7 text-[14.5px] text-[#6B5D52]" data-id="element-4785" data-mp="f727bj2ho1u6">
              Website change → ready to review
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-4786" data-mp="tvf3ag1ktkoo">
              <div className="flex gap-3" data-id="element-4787" data-mp="ty1c0j07e68h">
                <div className="w-8 h-8 rounded-full bg-[#D5E7E6] flex items-center justify-center text-[#526B6A] font-bold text-xs shrink-0" data-id="element-4788" data-mp="oxn2ewiyeo8c">
                  M
                </div>
                <div data-id="element-4789" data-mp="yvb09vbuwbuq">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-4790" data-mp="yw2vh51jk7kh">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-4791" data-mp="34nq38xw08pr6">
                      Mike
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-4792" data-mp="30nfytpnv27ef">11:32 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-4793" data-mp="2qjipzhyhtu9v">
                    <span className="text-[#EA580C] font-semibold" data-id="element-4794" data-mp="2uj9s2htgzwtp">@Giga</span>{' '}
                    the pricing page still says "$99/mo" on the starter tier. It
                    should be "$79/mo". Can you update it?
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-4795" data-mp="26sxasgcc4nqg">
                <div className="mt-1" data-id="element-4796" data-mp="2btbitydxrxoh">
                  <GigaAvatar size={32} data-id="element-4797" data-mp="2bucrxxbe6a3k" />
                </div>
                <div data-id="element-4798" data-mp="1xu24l49ajdn5">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-4799" data-mp="1xutz0br5j5bu">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-4800" data-mp="22slpd1c5ejdx">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-4801" data-mp="3s8uv4ovbhe88">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-4802" data-mp="3kbfh7a8ilnx0">11:38 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-4803" data-mp="3p1m495ndp0d5">
                    Done ✅ Updated the pricing page — Starter tier now shows
                    $79/mo. Preview is ready for your review:{' '}
                    <a href="#" className="text-[#EA580C] hover:underline" data-id="element-4804" data-mp="3f9q31lpzwu4p">
                      acme.com/pricing-preview
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="flex flex-col rounded-[22px] border border-[#E7E0D4] bg-white p-7 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)] md:col-span-2 lg:col-span-1" data-id="element-4805" data-mp="3u0gugsnodr7a">
            <span className="mb-3 text-[12px] font-bold uppercase tracking-[0.16em] text-[#EA580C]" data-id="element-4806" data-mp="2ik8riq4c0dv3">
              Full Projects
            </span>
            <h3 className="mb-2 font-title text-[28px] font-bold leading-none tracking-[-0.025em] text-[#2B211A]" data-id="element-4807" data-mp="2f3mdv8vmak8i">
              2,000+ credits
            </h3>
            <p className="mb-7 text-[14.5px] text-[#6B5D52]" data-id="element-4808" data-mp="2b216g31uqy5p">
              Deep research + presentation
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-4809" data-mp="2ozpmmxkz5h4f">
              <div className="flex gap-3" data-id="element-4810" data-mp="2k4aaqb6j1van">
                <div className="w-8 h-8 rounded-full bg-[#E7D5E4] flex items-center justify-center text-[#6B5268] font-bold text-xs shrink-0" data-id="element-4811" data-mp="1wdee90r4mbx2">
                  L
                </div>
                <div data-id="element-4812" data-mp="1rc8n37d46wpn">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-4813" data-mp="262xyoauhmdu1">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-4814" data-mp="265aofa6hlshn">
                      Lisa
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-4815" data-mp="1dmn8e96cl3nf">9:14 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-4816" data-mp="18o3jq10dddrd">
                    <span className="text-[#EA580C] font-semibold" data-id="element-4817" data-mp="38pa6bqp6xvz5">@Giga</span>{' '}
                    we need a competitive analysis — us vs Notion AI, Glean, and
                    Moveworks. Pricing, features, and positioning.
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-4818" data-mp="3mgeyk7q8blzg">
                <div className="mt-1" data-id="element-4819" data-mp="3mjax9y9wieoy">
                  <GigaAvatar size={32} data-id="element-4820" data-mp="2prz889fap8la" />
                </div>
                <div data-id="element-4821" data-mp="2ptk3605iutjo">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-4822" data-mp="2upsc2vt4lqie">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-4823" data-mp="2us5461sllh96">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-4824" data-mp="33ri2pzf6olf4">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-4825" data-mp="e40yp057zrlx">9:45 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-4826" data-mp="j2kml63djdgx">
                    I've compiled the research into a 12-slide deck. I analyzed
                    their public pricing, recent feature releases, and G2
                    reviews. Here's the draft:{' '}
                    <a href="#" className="text-[#EA580C] hover:underline font-medium" data-id="element-4827" data-mp="fe2yhoqchu5k">
                      
                      Competitive_Analysis_Q3.pdf
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}